import { apiClient } from '@/lib/apiClient';

export type ReferralStatus =
  | 'Requested' | 'Accepted' | 'Completed' | 'Declined' | 'NoShow' | 'Cancelled' | 'Expired';

export interface Session { accessToken: string; name: string; walletBalance: number; currency: string }

export interface WorklistItem {
  id: string;
  status: ReferralStatus;
  isUrgent: boolean;
  dueDate: string;
  createdAt: string;
  patientDisplayName: string;
  clinicName: string;
  summary: string;
  fileCount: number;
}

export interface RequestedStudy { modality: string; quantity: number; toothNumbers: string | null; note: string | null }
export interface ResultFile { id: string; fileName: string; contentType: string; sizeBytes: number | null; uploadedAt: string }

/** Everything the centre is shown. Mirrors the server DTO — deliberately no patient/tenant ids. */
export interface ReferralDetail {
  id: string;
  status: ReferralStatus;
  isUrgent: boolean;
  dueDate: string;
  createdAt: string;
  patientDisplayName: string;
  patientPhone: string;
  patientDateOfBirth: string | null;
  clinicalReason: string;
  studies: RequestedStudy[];
  clinicName: string;
  referringDoctorName: string;
  files: ResultFile[];
  centreReport: string | null;
  declineReason: string | null;
}

export const login = async (email: string, password: string) => {
  const { data } = await apiClient.post<Session>('/imaging/auth/login', { email, password });
  return data;
};

/** One-click entry to the demo sandbox — the same courtesy clinics get. */
export const demoLogin = async () => {
  const { data } = await apiClient.post<Session>('/imaging/auth/demo-login');
  return data;
};

export const getWorklist = async (status?: ReferralStatus) => {
  const { data } = await apiClient.get<{ data: WorklistItem[] }>('/imaging/referrals', {
    params: status ? { status } : undefined,
  });
  return data.data;
};

export const getReferral = async (id: string) => {
  const { data } = await apiClient.get<ReferralDetail>(`/imaging/referrals/${id}`);
  return data;
};

export const accept = (id: string) => apiClient.post(`/imaging/referrals/${id}/accept`);
export const decline = (id: string, reason: string) => apiClient.post(`/imaging/referrals/${id}/decline`, { reason });
export const markNoShow = (id: string) => apiClient.post(`/imaging/referrals/${id}/no-show`);
export const complete = (id: string, report: string | null) => apiClient.post(`/imaging/referrals/${id}/complete`, { report });

// ── Money ─────────────────────────────────────────────────────────────────────
//
// Two separate things, easily confused. The WALLET is what this centre pays the platform for
// delivering studies. STATEMENTS are what it owes each clinic in referral shares — money between
// two businesses that the platform records but never touches.

export interface WalletEntry {
  at: string; type: string; amount: number; balanceAfter: number; description: string | null;
}

export interface Wallet {
  balance: number;
  currency: string;
  /** What each delivered view currently costs. Zero while referrals are free. */
  studyFee: number;
  chargedThisMonth: number;
  recent: WalletEntry[];
}

export const getWallet = async () => {
  const { data } = await apiClient.get<Wallet>('/imaging/wallet');
  return data;
};

export interface BillingProfile {
  name: string;
  legalName: string | null;
  taxId: string | null;
  registrationNumber: string | null;
  iban: string | null;
  billingAddress: string | null;
  /** The registered city, which need not be the one patients are sent to. */
  billingCity: string | null;
  /** ISO 3166-2, e.g. RO-CJ — goes on the e-invoice, so it is typed rather than guessed. */
  billingCountySubdivision: string | null;
  isVatRegistered: boolean;
  /**
   * BT-10 — your own reference for our invoices: a purchase order, a cost centre, a contract
   * number. Whatever your accounts-payable system needs quoted back to route it.
   *
   * Yours to give, never ours to invent: a reference we made up would fail to route AND state
   * something untrue on a filed document.
   */
  buyerReference: string | null;
  /** Whether this centre can yet be named on a statement as a payable party. */
  isComplete: boolean;
  /** Which invoice-critical fields are still blank, so the screen can name them. */
  missingFields: string[];
}

export const getBillingProfile = async () => {
  const { data } = await apiClient.get<BillingProfile>('/imaging/billing-profile');
  return data;
};

export const updateBillingProfile = (input: Omit<BillingProfile, 'name' | 'isComplete' | 'missingFields'>) =>
  apiClient.put('/imaging/billing-profile', input);

export type ShareBasis = 'None' | 'PerStudy' | 'PerModality' | 'PercentOfAgreedTariff';

export interface PartnerRate { modality: string | null; amount: number | null; agreedTariff: number | null }

export interface ProposedTerms {
  id: string;
  clinicName: string;
  basis: ShareBasis;
  amountPerStudy: number | null;
  percentOfTariff: number | null;
  currency: string;
  effectiveFrom: string;
  description: string | null;
  /** Null means it is still a proposal — nothing accrues under it. */
  acceptedByCentreAt: string | null;
  rates: PartnerRate[];
}

export const getTerms = async () => {
  const { data } = await apiClient.get<{ data: ProposedTerms[] }>('/imaging/terms');
  return data.data;
};

export const respondToTerms = (termsId: string, accept: boolean, declineReason?: string | null) =>
  apiClient.post(`/imaging/terms/${termsId}/respond`, { accept, declineReason: declineReason ?? null });

export interface StatementLine {
  occurredOn: string; description: string; amount: number; isLatePosting: boolean;
}

export interface CentreStatement {
  id: string;
  clinicName: string;
  periodStart: string;
  periodEnd: string;
  openingBalance: number;
  charges: number;
  settlements: number;
  /** What is still to pay. Negative means this centre has paid ahead. */
  closingBalance: number;
  currency: string;
  series: string | null;
  number: number | null;
  status: 'Issued' | 'Disputed' | 'Acknowledged';
  issuedAt: string | null;
  disputeReason: string | null;
  lines: StatementLine[];
}

export interface ClinicOutstanding {
  tenantId: string; clinicName: string; outstanding: number; currency: string;
}

export interface CentreStatements {
  accounts: ClinicOutstanding[];
  statements: CentreStatement[];
}

export const getStatements = async () => {
  const { data } = await apiClient.get<CentreStatements>('/imaging/statements');
  return data;
};

export const respondToStatement = (statementId: string, accept: boolean, disputeReason?: string | null) =>
  apiClient.post(`/imaging/statements/${statementId}/respond`, {
    accept, disputeReason: disputeReason ?? null,
  });

export type SettlementMethod = 'BankTransfer' | 'Cash' | 'Card' | 'Offset' | 'Other';

/**
 * Tell a clinic you have paid them. Deliberately a claim rather than a payment: the clinic confirms
 * the money arrived before anything reaches the ledger, so neither side can close a shared balance
 * on its own word.
 */
export const declareSettlement = (
  tenantId: string,
  input: { amount: number; paidOn: string; method: SettlementMethod; reference?: string | null; note?: string | null },
) =>
  apiClient.post<{ id: string }>(`/imaging/clinics/${tenantId}/settlements`, {
    amount: input.amount,
    paidOn: input.paidOn,
    method: input.method,
    reference: input.reference ?? null,
    note: input.note ?? null,
  });

/**
 * Deliver one file. The URL is fetched immediately before the PUT rather than up front, so a slow
 * multi-file transfer can't outrun a signature minted minutes earlier — and the confirm afterwards
 * is what makes the study visible to the clinic at all.
 */
export const uploadResult = async (referralId: string, file: File) => {
  const { data } = await apiClient.post<{ fileId: string; uploadUrl: string; blobPath: string }>(
    `/imaging/referrals/${referralId}/files/upload-url`,
    { fileName: file.name, contentType: file.type || 'application/octet-stream' },
  );

  const put = await fetch(data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  await apiClient.post(`/imaging/referrals/${referralId}/files/${data.fileId}/confirm`, { sizeBytes: file.size });
  return data.fileId;
};

/**
 * Start a card top-up. The wallet is credited by the webhook when the payment clears, not here —
 * a centre that closes the tab after paying must still get its credit.
 *
 * This is what turns imaging from the platform's one postpaid stream into a prepaid one: instead of
 * a balance drifting negative and being chased, the centre buys credit and studies draw it down.
 */
export interface PayIntent { clientSecret: string; publishableKey: string }

export const topUpCard = async (amount: number) =>
  (await apiClient.post<PayIntent>('/imaging/wallet/topup/card', { amount })).data;

// ── Offboarding: take your data out, and leave ────────────────────────────────
//
// ⚠️ A centre owns strikingly little. Referrals, studies, partner terms and the images it uploaded
// are all TenantScopedEntity — the referring clinic's records, under that clinic's retention
// obligation. Closing a centre anonymises the centre and deletes its exports; it does not and must
// not touch the clinical work. The UI says so, because a centre that expected its uploads to vanish
// should hear why from us.

export interface ImagingExport {
  id: string;
  status: string;            // Pending | Running | Ready | Failed
  requestedAt: string;
  completedAt: string | null;
  expiresAt: string | null;
  sizeBytes: number | null;
  referralCount: number | null;
  error: string | null;
}

export const requestExport = async () =>
  (await apiClient.post<{ id: string }>('/imaging/export')).data;

export const getExports = async () =>
  (await apiClient.get<ImagingExport[]>('/imaging/exports')).data;

/** Short-lived pre-signed URL, fetched at click time — the archive never passes through the API. */
export const downloadExport = async (exportId: string) => {
  const { url } = (await apiClient.get<{ url: string }>(`/imaging/exports/${exportId}/url`)).data;
  window.location.href = url;
};

export interface ImagingClosureState {
  isClosing: boolean;
  requestedAt: string | null;
  dataDeletedOn: string | null;
  graceDays: number;
  dataAlreadyDeleted: boolean;
  /** Work still in flight. Shown before closing, because it does not stop — see AccountPage. */
  openReferrals: number;
}

export const getClosure = async () =>
  (await apiClient.get<ImagingClosureState>('/imaging/closure')).data;

/** `confirmation` must be the centre's name, typed exactly. Compared case-sensitively. */
export const requestClosure = async (confirmation: string) =>
  (await apiClient.post<ImagingClosureState>('/imaging/closure', { confirmation })).data;

export const cancelClosure = async () =>
  (await apiClient.delete<ImagingClosureState>('/imaging/closure')).data;
